import { useEffect, useRef } from 'react';
import type { Message } from '../types';

export function useUnreadMessages(
  minimized: boolean,
  messages: Message[],
  uiDispatch: (action: { type: 'RESET_UNREAD' | 'INCREMENT_UNREAD' }) => void
): void {
  // Opening the panel means the visitor is looking at it: clear the badge.
  useEffect(() => {
    if (!minimized) uiDispatch({ type: 'RESET_UNREAD' });
  }, [minimized, uiDispatch]);

  // Badge the launcher for every assistant message that lands while the panel is
  // closed (streamed replies, JSON replies, polled agent replies). A ref keeps the
  // check current without re-running the effect on every open/close.
  const minimizedRef = useRef(minimized);
  useEffect(() => {
    minimizedRef.current = minimized;
  }, [minimized]);
  const seenMessageIdsRef = useRef<Set<string> | null>(null);
  useEffect(() => {
    const seen = seenMessageIdsRef.current;
    seenMessageIdsRef.current = new Set(messages.map(m => m.id));
    // First paint and history/session restores start from an empty list: nothing is new.
    if (!seen || seen.size === 0 || !minimizedRef.current) return;
    for (const message of messages) {
      if (seen.has(message.id)) continue;
      if (message.sender !== 'ai' || message.localOnly) continue;
      if (typeof message.id === 'string' && message.id.startsWith('intro-')) continue;
      uiDispatch({ type: 'INCREMENT_UNREAD' });
    }
  }, [messages, uiDispatch]);
}
